/**
 * DynamicFieldRenderer
 * Renders individual form fields based on their schema type
 */

import React, { useState } from 'react';
import { Images } from 'lucide-react';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface DynamicFieldRendererProps {
  field: FieldSchema;
  value: any;
  onChange: (value: any) => void;
  formData?: any;  // Access to full form data for photo fields
  onFormDataChange?: (fieldId: string, value: any) => void;  // Update form data
  onPhotoUpload?: (files: File[]) => Promise<void>;
  error?: string;
  readOnly?: boolean;
}

export default function DynamicFieldRenderer({
  field,
  value,
  onChange,
  formData = {},
  onFormDataChange,
  onPhotoUpload,
  error,
  readOnly = false,
}: DynamicFieldRendererProps) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  
  // Helper to render photo capture for fields with enablePhotoCapture
  const renderPhotoCapture = () => {
    if (!(field as any).enablePhotoCapture || !onFormDataChange) return null;
    
    const photosFieldId = `${field.id}_photos`;
    const photos = Array.isArray(formData[photosFieldId]) ? formData[photosFieldId] : [];
    
    // Helper to format image URLs (handle both strings and objects)
    const formatImageUrl = (photoData: any) => {
      if (!photoData) return '';
      
      // If it's an object (from PropertyRear upload), extract url or dataUrl
      if (typeof photoData === 'object') {
        const url = photoData.url || photoData.dataUrl || photoData.cdnUrl || '';
        if (url) return url;
      }
      
      // If it's a string (from Initial Questionnaire)
      if (typeof photoData === 'string') {
        if (photoData.startsWith('data:') || photoData.startsWith('http')) return photoData;
        return `data:image/webp;base64,${photoData}`;
      }
      
      return '';
    };
    
    // Handle photo deletion
    const handleDeletePhoto = async (photoId: string) => {
      console.log(`[DynamicFieldRenderer] 🗑️ Deleting photo ${photoId}`);
      console.log(`[DynamicFieldRenderer] 🗑️ Total photos before deletion:`, photos.length);
      console.log(`[DynamicFieldRenderer] 🗑️ Photos:`, photos);
      
      // Find the photo to get its fileName for storage deletion
      const photoIndex = photos.findIndex((p: any, idx: number) => {
        const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
        return id === photoId;
      });
      
      if (photoIndex === -1) {
        console.error(`[DynamicFieldRenderer] ❌ Photo ${photoId} not found`);
        return;
      }
      
      const photoToDelete = photos[photoIndex];
      console.log(`[DynamicFieldRenderer] 🗑️ Found photo to delete at index ${photoIndex}:`, photoToDelete);
      
      // Try to delete from storage if it's a CDN photo
      if (photoToDelete && typeof photoToDelete === 'object') {
        let filePathToDelete = photoToDelete.fileName;
        
        // If no fileName but has URL, try to extract path from CDN URL
        if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
          const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
          if (urlParts) {
            const [bucket, ...pathParts] = urlParts.split('/');
            filePathToDelete = pathParts.join('/');
            console.log(`[DynamicFieldRenderer] 🗑️ Extracted path from URL: ${filePathToDelete}`);
          }
        }
        
        if (filePathToDelete) {
          try {
            console.log(`[DynamicFieldRenderer] 🗑️ Deleting from storage: ${filePathToDelete}`);
            await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
            console.log(`[DynamicFieldRenderer] ✅ Deleted from storage`);
          } catch (error) {
            console.warn(`[DynamicFieldRenderer] ⚠️ Failed to delete from storage:`, error);
            // Continue with state deletion even if storage deletion fails
          }
        } else {
          console.log(`[DynamicFieldRenderer] ℹ️ No fileName/URL found, skipping storage deletion (local photo only)`);
        }
      }
      
      // Remove from state
      const updatedPhotos = photos.filter((p: any, idx: number) => {
        const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
        return id !== photoId;
      });
      console.log(`[DynamicFieldRenderer] ✅ Deleted photo ${photoId}, ${updatedPhotos.length} remaining`);
      onFormDataChange(photosFieldId, updatedPhotos);
    };
    
    // Handle photo note/caption update
    const handleUpdateNote = (photoId: string, note: string) => {
      const updatedPhotos = photos.map((p: any, idx: number) => {
        const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
        if (id === photoId) {
          // If it's a string, convert to object to add note
          if (typeof p === 'string') {
            return { dataUrl: p, note, id: photoId };
          }
          // If it's an object, add/update note
          return { ...p, note };
        }
        return p;
      });
      console.log(`[DynamicFieldRenderer] Updated note for photo ${photoId}`);
      onFormDataChange(photosFieldId, updatedPhotos);
    };
    
    // Transform photos for gallery modal
    const galleryPhotos = photos.map((p: any, idx: number) => {
      const isObject = typeof p === 'object';
      const photoUrl = formatImageUrl(p);
      const photoId = isObject ? (p.displayId || p.id || idx.toString()) : idx.toString();
      
      return {
        id: photoId,
        url: photoUrl,
        note: isObject ? p.note : undefined,
        caption: isObject ? p.caption : undefined,
        uploading: isObject ? p.uploading : false,
      };
    }).filter(p => p.url); // Only include photos with valid URLs
    
    return (
      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-gray-600 mb-2">📸 Attached Photos</p>
        <PhotoCapture
          onPhotosCaptured={(newPhotos: any[]) => {
            console.log('[DynamicFieldRenderer] ========== PHOTO CAPTURE START ==========');
            console.log('[DynamicFieldRenderer] 📸 Field ID:', field.id);
            console.log('[DynamicFieldRenderer] 📸 Photos field ID:', photosFieldId);
            console.log('[DynamicFieldRenderer] 📸 Captured photos count:', newPhotos.length);
            console.log('[DynamicFieldRenderer] 📸 Captured photos:', newPhotos);
            console.log('[DynamicFieldRenderer] 📸 Existing photos:', photos);
            
            const newPhotoUrls = newPhotos.map((p, idx) => {
              let url;
              if (p.base64String?.startsWith('data:')) {
                url = p.base64String;
              } else {
                const format = p.format || 'webp';
                url = `data:image/${format};base64,${p.base64String}`;
              }
              console.log(`[DynamicFieldRenderer] 📸 Photo ${idx} formatted:`, url.substring(0, 50) + '...');
              return url;
            });
            
            const updatedPhotos = [...photos, ...newPhotoUrls];
            console.log('[DynamicFieldRenderer] 📸 Total photos after update:', updatedPhotos.length);
            console.log('[DynamicFieldRenderer] 📸 Calling onFormDataChange with:', photosFieldId, '=', updatedPhotos.length, 'photos');
            console.log('[DynamicFieldRenderer] ========== CALLING onFormDataChange ==========');
            
            onFormDataChange(photosFieldId, updatedPhotos);
            
            console.log('[DynamicFieldRenderer] ========== PHOTO CAPTURE END ==========');
          }}
          maxPhotos={(field as any).maxPhotos || 10}
          buttonLabel="Attach Photos"
          allowUpload={true}
        />
        {photos.length > 0 && (() => {
          // Find the last valid photo
          const lastValidPhoto = [...photos].reverse().find(p => {
            const url = formatImageUrl(p);
            return url && url.length > 0;
          });
          
          if (!lastValidPhoto) return null;
          
          const imageUrl = formatImageUrl(lastValidPhoto);
          if (!imageUrl) return null;
          
          return (
            <>
              <button
                type="button"
                onClick={() => setGalleryOpen(true)}
                className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
              >
                <img
                  src={imageUrl}
                  alt={`${field.label} photos`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                  <Images size={12} />
                  {photos.length}
                </div>
              </button>
              
              {/* Photo Gallery Modal */}
              {galleryOpen && galleryPhotos.length > 0 && (
                <PhotoGalleryModal
                  isOpen={galleryOpen}
                  onClose={() => setGalleryOpen(false)}
                  photos={galleryPhotos}
                  sectionTitle={`${field.label} - Photos`}
                  onDeletePhoto={handleDeletePhoto}
                  onUpdateNote={handleUpdateNote}
                />
              )}
            </>
          );
        })()}
      </div>
    );
  };
  // Render based on field type
  switch (field.type) {
    case 'text':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={readOnly}
            maxLength={field.maxLength}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${readOnly ? 'bg-gray-100' : ''}`}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={readOnly}
            rows={field.rows || 4}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${readOnly ? 'bg-gray-100' : ''}`}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {field.unit && <span className="text-gray-500 ml-1">({field.unit})</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            placeholder={field.placeholder}
            disabled={readOnly}
            min={field.min}
            max={field.max}
            step={field.step}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${readOnly ? 'bg-gray-100' : ''}`}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'date':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${readOnly ? 'bg-gray-100' : ''}`}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'dropdown':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${readOnly ? 'bg-gray-100' : ''}`}
          >
            <option value="">-- Select --</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'multiChoice':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <div className="space-y-2">
            {field.options?.map((option) => {
              const isSelected = field.allowMultiple
                ? (value || []).includes(option.value)
                : value === option.value;

              const handleChange = () => {
                if (readOnly) return;

                if (field.allowMultiple) {
                  const currentValues = value || [];
                  if (isSelected) {
                    onChange(currentValues.filter((v: string) => v !== option.value));
                  } else {
                    onChange([...currentValues, option.value]);
                  }
                } else {
                  onChange(option.value);
                }
              };

              return (
                <label
                  key={option.value}
                  className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type={field.allowMultiple ? 'checkbox' : 'radio'}
                    checked={isSelected}
                    onChange={handleChange}
                    disabled={readOnly}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-900">{option.label}</span>
                </label>
              );
            })}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      );

    case 'boolean':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <div className="flex space-x-4">
            {field.displayStyle === 'toggle' ? (
              <button
                type="button"
                onClick={() => !readOnly && onChange(!value)}
                disabled={readOnly}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-blue-600' : 'bg-gray-300'
                } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => !readOnly && onChange(true)}
                  disabled={readOnly}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    value === true
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => !readOnly && onChange(false)}
                  disabled={readOnly}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    value === false
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  No
                </button>
                {field.displayStyle === 'yes-no-na' && (
                  <button
                    type="button"
                    onClick={() => !readOnly && onChange(null)}
                    disabled={readOnly}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      value === null
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    N/A
                  </button>
                )}
              </>
            )}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <div className={`space-y-2 ${field.displayStyle === 'inline' ? 'flex flex-wrap gap-4' : field.displayStyle === 'grid' ? 'grid grid-cols-2 gap-3' : ''}`}>
            {field.options?.map((option) => {
              const isSelected = value === option.value;
              return (
                <label
                  key={option.value}
                  className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={isSelected}
                    onChange={() => !readOnly && onChange(option.value)}
                    disabled={readOnly}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-900">{option.label}</span>
                </label>
              );
            })}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'checkbox':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          <div className={`space-y-2 ${field.displayStyle === 'inline' ? 'flex flex-wrap gap-4' : field.displayStyle === 'grid' ? 'grid grid-cols-2 gap-3' : ''}`}>
            {field.options?.map((option) => {
              const currentValues = value || [];
              const isSelected = currentValues.includes(option.value);
              
              const handleChange = () => {
                if (readOnly) return;
                if (isSelected) {
                  onChange(currentValues.filter((v: string) => v !== option.value));
                } else {
                  onChange([...currentValues, option.value]);
                }
              };

              return (
                <label
                  key={option.value}
                  className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={handleChange}
                    disabled={readOnly}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-900">{option.label}</span>
                </label>
              );
            })}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {renderPhotoCapture()}
        </div>
      );

    case 'signature':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          
          {!value ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-sm text-gray-500">Signature capture coming soon</p>
              <p className="text-xs text-gray-400 mt-1">Use text field for now</p>
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <img
                src={value}
                alt="Signature"
                className="w-full h-32 object-contain bg-white"
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="w-full py-2 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100"
                >
                  Clear Signature
                </button>
              )}
            </div>
          )}
          
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      );

    case 'image':
      // Use consistent photo handling - store in formData directly
      const currentPhotos = Array.isArray(value) ? value : [];
      
      // Helper to format image URLs
      const formatImageUrl = (photoData: any) => {
        if (!photoData) return '';
        
        if (typeof photoData === 'object') {
          return photoData.url || photoData.dataUrl || photoData.cdnUrl || '';
        }
        
        if (typeof photoData === 'string') {
          if (photoData.startsWith('data:') || photoData.startsWith('http')) return photoData;
          return `data:image/webp;base64,${photoData}`;
        }
        
        return '';
      };
      
      // Handle photo deletion
      const handleDeleteImagePhoto = async (photoId: string) => {
        console.log(`[DynamicFieldRenderer] 🗑️ Deleting image photo ${photoId}`);
        console.log(`[DynamicFieldRenderer] 🗑️ Total photos before deletion:`, currentPhotos.length);
        
        // Find the photo to get its fileName for storage deletion
        const photoIndex = currentPhotos.findIndex((p: any, idx: number) => {
          const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
          return id === photoId;
        });
        
        if (photoIndex === -1) {
          console.error(`[DynamicFieldRenderer] ❌ Photo ${photoId} not found`);
          return;
        }
        
        const photoToDelete = currentPhotos[photoIndex];
        console.log(`[DynamicFieldRenderer] 🗑️ Found photo to delete at index ${photoIndex}:`, photoToDelete);
        
        // Try to delete from storage if it's a CDN photo
        if (photoToDelete && typeof photoToDelete === 'object') {
          let filePathToDelete = photoToDelete.fileName;
          
          // If no fileName but has URL, try to extract path from CDN URL
          if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
            const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
            if (urlParts) {
              const [bucket, ...pathParts] = urlParts.split('/');
              filePathToDelete = pathParts.join('/');
              console.log(`[DynamicFieldRenderer] 🗑️ Extracted path from URL: ${filePathToDelete}`);
            }
          }
          
          if (filePathToDelete) {
            try {
              console.log(`[DynamicFieldRenderer] 🗑️ Deleting from storage: ${filePathToDelete}`);
              await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
              console.log(`[DynamicFieldRenderer] ✅ Deleted from storage`);
            } catch (error) {
              console.warn(`[DynamicFieldRenderer] ⚠️ Failed to delete from storage:`, error);
              // Continue with state deletion even if storage deletion fails
            }
          } else {
            console.log(`[DynamicFieldRenderer] ℹ️ No fileName/URL found, skipping storage deletion (local photo only)`);
          }
        }
        
        // Remove from state
        const updatedPhotos = currentPhotos.filter((p: any, idx: number) => {
          const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
          return id !== photoId;
        });
        console.log(`[DynamicFieldRenderer] ✅ Deleted photo ${photoId}, ${updatedPhotos.length} remaining`);
        onChange(updatedPhotos);
      };
      
      // Handle photo note update
      const handleUpdateImageNote = (photoId: string, note: string) => {
        const updatedPhotos = currentPhotos.map((p: any, idx: number) => {
          const id = typeof p === 'object' ? (p.displayId || p.id || idx.toString()) : idx.toString();
          if (id === photoId) {
            if (typeof p === 'string') {
              return { dataUrl: p, note, id: photoId };
            }
            return { ...p, note };
          }
          return p;
        });
        onChange(updatedPhotos);
      };
      
      // Transform photos for gallery modal
      const galleryPhotos = currentPhotos.map((p: any, idx: number) => {
        const isObject = typeof p === 'object';
        const photoUrl = formatImageUrl(p);
        const photoId = isObject ? (p.displayId || p.id || idx.toString()) : idx.toString();
        
        return {
          id: photoId,
          url: photoUrl,
          note: isObject ? p.note : undefined,
          caption: isObject ? p.caption : undefined,
          uploading: isObject ? p.uploading : false,
        };
      }).filter(p => p.url);
      
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {field.maxImages && (
              <span className="text-gray-500 ml-1">(Max: {field.maxImages})</span>
            )}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500">{field.helpText}</p>
          )}
          
          {/* Photo Capture Component */}
          <PhotoCapture
            onPhotosCaptured={(newPhotos: any[]) => {
              const newPhotoUrls = newPhotos.map((p) => {
                let url;
                if (p.base64String?.startsWith('data:')) {
                  url = p.base64String;
                } else {
                  const format = p.format || 'webp';
                  url = `data:image/${format};base64,${p.base64String}`;
                }
                return url;
              });
              
              const updatedPhotos = [...currentPhotos, ...newPhotoUrls];
              onChange(updatedPhotos);
            }}
            maxPhotos={field.maxImages || 10}
            buttonLabel="Attach Photos"
            allowUpload={true}
          />
          
          {/* Photo Preview with Gallery */}
          {currentPhotos.length > 0 && (() => {
            const lastValidPhoto = [...currentPhotos].reverse().find(p => {
              const url = formatImageUrl(p);
              return url && url.length > 0;
            });
            
            if (!lastValidPhoto) return null;
            
            const imageUrl = formatImageUrl(lastValidPhoto);
            if (!imageUrl) return null;
            
            return (
              <>
                <button
                  type="button"
                  onClick={() => setGalleryOpen(true)}
                  className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
                >
                  <img
                    src={imageUrl}
                    alt={`${field.label} photos`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                    <Images size={12} />
                    {currentPhotos.length}
                  </div>
                </button>
                
                {/* Photo Gallery Modal */}
                {galleryOpen && galleryPhotos.length > 0 && (
                  <PhotoGalleryModal
                    isOpen={galleryOpen}
                    onClose={() => setGalleryOpen(false)}
                    photos={galleryPhotos}
                    sectionTitle={`${field.label} - Photos`}
                    onDeletePhoto={handleDeleteImagePhoto}
                    onUpdateNote={handleUpdateImageNote}
                  />
                )}
              </>
            );
          })()}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      );

    default:
      return (
        <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
          <p className="text-sm text-gray-600">
            Unsupported field type: {(field as any).type}
          </p>
        </div>
      );
  }
}
